import React, { FunctionComponent, ReactElement } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { renderFont } from '@empiriecom/module-components/ThemeProvider';
import { Availability, DimensionValue } from '@/interfaces';
import { SetProduct } from '@/src/components/ProductSelection/types';

const PickerTileWrapper = styled.div<{
  isSelected: boolean;
  isMobile: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  margin: 0 0.5rem 0.5rem 0;
  ${({ isSelected, theme }) =>
    isSelected ? renderFont('base', 'bold', theme) : renderFont('base', 'normal', theme)};
  &&:last-child {
    ${({ isMobile }) => isMobile && 'margin-right: 0;'};
  }
`;

const PickerTileContainer = styled.div<{
  isSmallTile: boolean;
  isSelected: boolean;
  rgb: string;
  image: string;
  isColorTile: boolean;
  isAvailable: boolean;
}>`
  ${({ isSmallTile }) => isSmallTile && 'width: 3.5rem;'}
  height: 3.5rem;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  border-radius: 5px;
  ${({ isSelected, isAvailable }) =>
    isAvailable &&
    (isSelected
      ? 'border-style: solid; border-color: #444444; border-width: 2px;'
      : 'border-style: solid; border-color: #444444; border-width: 1px;')};
  box-shadow: ${({ isSelected }) =>
    isSelected
      ? '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 1px 10px 0 rgba(0, 0, 0, 0.12), 0 4px 5px 0 rgba(0, 0, 0, 0.14);'
      : '0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14);'};
  ${({ isSmallTile }) => !isSmallTile && 'padding: 0 1rem;'};
  background: ${({ rgb, image, isColorTile }) =>
    isColorTile ? `#${rgb} url(${image}) center / 100% 100%` : '#ffffff'};
  ${({ isAvailable }) => !isAvailable && 'text-decoration: line-through; color:#9b9b9b;'};
`;

const PickerTileName = styled.div`
  font-size: 0.6875rem;
  text-align: center;
  width: 3.5rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

interface PickerTileProps extends Partial<DimensionValue> {
  availability: Availability;
  value: DimensionValue;
  setProduct: SetProduct;
  axisType: string;
  inputType: string;
}

export const PickerTile: FunctionComponent<PickerTileProps> = ({
  availability = { state: '' },
  value,
  setProduct,
  axisType,
  inputType,
}): ReactElement => {
  const isMobile = useMedia({
    desktop: false,
    mobile: true,
    tablet: false,
  });
  const isColorTile = axisType === 'COLOR';
  const isSmallTile = isColorTile || (inputType === 'TILES' && axisType === 'SIZE');
  const { state } = availability;

  return (
    <PickerTileWrapper
      onClick={() => !value.isSelected && setProduct(value.sku)}
      isSelected={value.isSelected}
      isMobile={isMobile}
      data-testid="picker-tile"
      data-selected={value.isSelected}
      title={value.displayText}
    >
      <PickerTileContainer
        isSmallTile={isSmallTile}
        isSelected={value.isSelected}
        rgb={value.rgb}
        image={value.image}
        isColorTile={isColorTile}
        isAvailable={state !== 'NOT_AVAILABLE'}
      >
        {!isColorTile && <span className="picker-tile-value">{value.displayText}</span>}
      </PickerTileContainer>
      {isColorTile && <PickerTileName>{value.displayText}</PickerTileName>}
    </PickerTileWrapper>
  );
};

export default PickerTile;
